import { useEffect, useId, type InputHTMLAttributes, type ReactNode, type Ref, type SelectHTMLAttributes } from 'react';
import { useBlocker } from 'react-router';

/** Classe base dos campos: retos, 44px no toque, foco Aco, erro em vermelho. */
export const classeCampo = (erro?: boolean) =>
  `w-full rounded-(--radius-controle) border bg-superficie px-3 py-2 text-[15px] outline-none placeholder:text-apagado focus:border-destaque disabled:opacity-60 ${
    erro ? 'border-erro' : 'border-borda-forte'
  }`;

/**
 * Rotulo em cima, ajuda embaixo, erro no lugar da ajuda. O filho recebe o id
 * para ligar rotulo e descricao (acessibilidade).
 */
export function Campo({
  rotulo,
  obrigatorio,
  ajuda,
  erro,
  children,
  className = '',
}: {
  rotulo: string;
  obrigatorio?: boolean;
  ajuda?: ReactNode;
  erro?: string | undefined;
  children: (props: { id: string; 'aria-describedby'?: string; 'aria-invalid'?: boolean }) => ReactNode;
  className?: string;
}) {
  const id = useId();
  const descricao = erro || ajuda ? `${id}-desc` : undefined;
  return (
    <div className={`space-y-1.5 ${className}`}>
      <label htmlFor={id} className="block text-sm font-medium">
        {rotulo}
        {obrigatorio && (
          <span className="ml-0.5 text-erro" aria-hidden>
            *
          </span>
        )}
        {obrigatorio && <span className="sr-only"> (obrigatório)</span>}
      </label>
      {children({ id, ...(descricao ? { 'aria-describedby': descricao } : {}), ...(erro ? { 'aria-invalid': true } : {}) })}
      {(erro || ajuda) && (
        <p id={descricao} className={`text-xs ${erro ? 'text-erro' : 'text-apagado'}`}>
          {erro ?? ajuda}
        </p>
      )}
    </div>
  );
}

export function CampoTexto({
  rotulo,
  obrigatorio,
  ajuda,
  erro,
  className,
  ref,
  ...p
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> & {
  rotulo: string;
  obrigatorio?: boolean;
  ajuda?: ReactNode;
  erro?: string | undefined;
  ref?: Ref<HTMLInputElement>;
}) {
  return (
    <Campo rotulo={rotulo} obrigatorio={obrigatorio ?? false} ajuda={ajuda} erro={erro} className={className ?? ''}>
      {(a) => <input ref={ref} {...a} {...p} className={classeCampo(!!erro)} />}
    </Campo>
  );
}

export function Selecao({
  rotulo,
  obrigatorio,
  ajuda,
  erro,
  className,
  children,
  ...p
}: Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> & {
  rotulo: string;
  obrigatorio?: boolean;
  ajuda?: ReactNode;
  erro?: string | undefined;
  children: ReactNode;
}) {
  return (
    <Campo rotulo={rotulo} obrigatorio={obrigatorio ?? false} ajuda={ajuda} erro={erro} className={className ?? ''}>
      {(a) => (
        <select {...a} {...p} className={classeCampo(!!erro)}>
          {children}
        </select>
      )}
    </Campo>
  );
}

/** Escolha unica em botoes retos lado a lado (sexo, marcha, nivel...). */
export function OpcoesRadio<T extends string>({
  rotulo,
  obrigatorio,
  valor,
  onMudar,
  opcoes,
  erro,
  ajuda,
  tamanho = 'md',
}: {
  rotulo: string;
  obrigatorio?: boolean;
  valor: T | '';
  onMudar: (v: T) => void;
  opcoes: readonly { valor: T; rotulo: string }[];
  erro?: string | undefined;
  ajuda?: ReactNode;
  tamanho?: 'md' | 'sm';
}) {
  const id = useId();
  // Muitas opcoes de texto longo: no celular viram grade de 2 colunas, em vez
  // de quebrar a linha deixando uma opcao sozinha. Opcoes curtas seguem coladas.
  const emGrade = opcoes.length > 3 && opcoes.reduce((n, o) => n + o.rotulo.length, 0) > 24;
  const borda = erro ? 'border-erro' : 'border-borda-forte';
  return (
    <fieldset className="space-y-1.5" aria-describedby={erro || ajuda ? `${id}-desc` : undefined}>
      <legend className={tamanho === 'sm' ? 'sr-only' : 'mb-1.5 text-sm font-medium'}>
        {rotulo}
        {obrigatorio && (
          <span className="ml-0.5 text-erro" aria-hidden>
            *
          </span>
        )}
      </legend>
      <div
        role="radiogroup"
        className={
          emGrade
            ? `grid grid-cols-2 gap-1.5 sm:inline-flex sm:flex-wrap sm:gap-0 sm:rounded-(--radius-controle) sm:border ${borda}`
            : `inline-flex flex-wrap rounded-(--radius-controle) border ${borda}`
        }
      >
        {opcoes.map((o, i) => {
          const ativo = o.valor === valor;
          return (
            <button
              key={o.valor}
              type="button"
              role="radio"
              aria-checked={ativo}
              onClick={() => onMudar(o.valor)}
              className={`px-3 font-medium select-none transition-colors ${tamanho === 'sm' ? 'text-[13px]' : 'text-sm'} ${
                emGrade
                  ? `rounded-(--radius-controle) border border-borda-forte sm:rounded-none sm:border-0 ${i > 0 ? 'sm:border-l' : ''}`
                  : i > 0
                    ? 'border-l border-borda-forte'
                    : ''
              } ${ativo ? 'bg-marca text-sobre-marca' : 'bg-superficie text-texto hover:bg-destaque-fundo'}`}
            >
              {o.rotulo}
            </button>
          );
        })}
      </div>
      {(erro || ajuda) && (
        <p id={`${id}-desc`} className={`text-xs ${erro ? 'text-erro' : 'text-apagado'}`}>
          {erro ?? ajuda}
        </p>
      )}
    </fieldset>
  );
}

/** Resumo dos erros no topo do formulario, com link para cada campo. */
export function ResumoErros({ erros }: { erros: readonly { campo: string; mensagem: string }[] }) {
  if (erros.length === 0) return null;
  return (
    <div role="alert" className="border-l-2 border-erro bg-erro/8 px-4 py-3 text-sm">
      <p className="font-semibold text-erro">
        {erros.length === 1 ? 'Corrija 1 campo para salvar:' : `Corrija ${erros.length} campos para salvar:`}
      </p>
      <ul className="mt-1 list-inside list-disc text-texto">
        {erros.map((e) => (
          <li key={`${e.campo}-${e.mensagem}`}>{e.mensagem}</li>
        ))}
      </ul>
    </div>
  );
}

/** Barra de acoes presa no rodape do formulario: sempre a mao, mesmo em formulario longo. */
/**
 * Acoes do formulario, presas ao pe da tela. So aparece em tela de tarefa, que
 * esconde a navegacao de baixo (ver Layout): por isso fica em bottom-0, com a
 * margem da barra do sistema. No celular os botoes ficam numa linha so, com o
 * principal (o ultimo) ocupando o que sobra; o aviso vai para a linha de cima.
 */
export function BarraAcoes({ children, aviso }: { children: ReactNode; aviso?: ReactNode }) {
  return (
    <div className="sticky bottom-0 z-10 -mx-4 flex flex-wrap items-center justify-end gap-x-2 gap-y-1.5 border-t border-borda bg-fundo/95 px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] backdrop-blur lg:-mx-8 lg:px-8">
      {aviso && <div className="w-full text-xs text-apagado sm:mr-auto sm:w-auto">{aviso}</div>}
      <div className="flex w-full items-center justify-end gap-2 sm:w-auto max-sm:[&>*:last-child]:flex-1">{children}</div>
    </div>
  );
}

/**
 * Pergunta antes de sair com alteracoes nao salvas — fechando a aba ou
 * navegando dentro do app.
 */
export function useAvisoSaida(sujo: boolean) {
  const bloqueio = useBlocker(sujo);
  useEffect(() => {
    if (bloqueio.state !== 'blocked') return;
    if (window.confirm('Há alterações não salvas. Sair mesmo assim?')) bloqueio.proceed();
    else bloqueio.reset();
  }, [bloqueio]);
  useEffect(() => {
    if (!sujo) return;
    const aviso = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', aviso);
    return () => window.removeEventListener('beforeunload', aviso);
  }, [sujo]);
}
